import { Box, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { useEmployee, useEmployeeLoginData } from '~/data/employee';
import { useEmployeeProfile } from '~/data/employeeProfile';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { AccountManagementMenu_OnUpdateLegacy } from '~/library/AccountManagementMenu/component';
import { PasswordSubmitMethod } from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm/data';
import { usePasswordSubmitMethods } from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm/usePasswordSubmitMethods';
import { usePasswordSubmitMethodValidationSchema } from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm/usePasswordSubmitMethodValidationSchema';

import { ResetPasswordForm, ResetPasswordType, postRestPassword } from './data';

export type useEmployeeDataOutput = {
  form: UseFormReturn<ResetPasswordForm>;
  onSubmit: () => void;
  isFormEnabled: boolean;
  employeeNationality?: string;
  dialogTitle: string;
  onClose: () => void;
};

export type UseResetPasswordProps = {
  onClose: () => void;
} & AccountManagementMenu_OnUpdateLegacy;

export const useResetPassword = (
  props: UseResetPasswordProps,
): useEmployeeDataOutput => {
  const { onClose, onUpdateLegacy } = props;

  const theme = useTheme();

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { getPasswordSubmitMethods } = usePasswordSubmitMethods();

  // Dialog Title
  const { profile } = useEmployeeProfile();
  const { dialogTitle, employeeFullName } = useMemo(() => {
    const dialogName = t('laResetPassword');
    const employeeFullName = !!profile
      ? `${profile.firstName} ${profile.lastName}`
      : '';
    const dialogTitle = !!employeeFullName
      ? `${dialogName} - ${employeeFullName}`
      : dialogName;

    return {
      dialogTitle,
      employeeFullName,
    };
  }, [t, profile]);

  // Confirm Dialog
  const confirm = useConfirmDialog();
  const confirmPayload = useCallback(
    (
      payload: ResetPasswordType,
      data: ResetPasswordForm,
    ): Promise<ResetPasswordType> => {
      const {
        passwordSubmit: { method, workEmail, mobile, alternateEmail },
      } = data;

      const submitMethod = () => {
        switch (method) {
          case PasswordSubmitMethod.WorkEmail:
            return `${t('email')}: ${workEmail}`;
          case PasswordSubmitMethod.SMS:
            return `${t('mobile')}: ${mobile}`;
          case PasswordSubmitMethod.AlternateEmail:
            return `${t('email')}: ${alternateEmail}`;
          default:
            return <></>;
        }
      };

      return new Promise(
        (resolve: (value: ResetPasswordType) => void, reject) => {
          confirm(
            <>
              <Box sx={{ marginBottom: theme.spacing(1) }}>
                {t('laConfirmPasswordReset')}
              </Box>
              <Box>{employeeFullName}</Box>
              <Box>{submitMethod()}</Box>
            </>,
            (confirmed) => {
              if (confirmed) {
                return resolve(payload);
              }

              return reject();
            },
          );
        },
      );
    },
    [t, confirm, employeeFullName, theme],
  );

  const { employee } = useEmployee();
  const { employeeAdminLoginData: employeeLoginData } = useEmployeeLoginData();

  // Validation
  const yupPasswordSubmitSchema = usePasswordSubmitMethodValidationSchema();

  const validationSchema = useMemo(() => {
    return yup.object().shape<ResetPasswordForm>({
      userName: yup.string(),
      passwordSubmit: yupPasswordSubmitSchema.defined(),
    });
  }, [yupPasswordSubmitSchema]);

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  // Form
  const { defaultValues, isMethodsAvailable, employeeNationality } =
    useMemo(() => {
      const employeeNationality = employee.empBasicData.fEmpNationality;
      const workEmail = employee.empWorkContact.fEmpEmailWork;

      const { defaultMethod, isMethodsAvailable } =
        getPasswordSubmitMethods(workEmail);

      const defaultValues: ResetPasswordForm = {
        userName: employeeLoginData.fLoginUsername,
        passwordSubmit: {
          method: defaultMethod,
          workEmail: workEmail ?? '',
          mobile: '',
          alternateEmail: '',
        },
      };

      return {
        defaultValues,
        isMethodsAvailable,
        employeeNationality,
      };
    }, [getPasswordSubmitMethods, employee, employeeLoginData]);

  const form = useForm<ResetPasswordForm>({
    resolver,
    defaultValues,
  });

  const { handleSubmit, reset } = form;

  const hasEmployeeChanged = useHasChanged(
    JSON.stringify({
      employeeId: employee.empBasicData.fEmpId,
      workEmail: employee.empWorkContact.fEmpEmailWork,
      userName: employeeLoginData.fLoginUsername,
      inSync: employeeLoginData.fLoginEmpId === employee.empBasicData.fEmpId,
    }),
  );
  useEffect(() => {
    if (hasEmployeeChanged) {
      reset(defaultValues);
    }
  }, [reset, defaultValues, hasEmployeeChanged]);

  const handleClose = useCallback(() => {
    reset(defaultValues);
    onClose();
  }, [onClose, reset, defaultValues]);

  const onSubmitSuccess = useCallback(() => {
    if (!!onUpdateLegacy) {
      onUpdateLegacy(['resetPassword']);
    }
    handleClose();
  }, [onUpdateLegacy, handleClose]);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const {
        userName: fLoginUsername,
        passwordSubmit: { method, workEmail, mobile, alternateEmail },
      } = data;

      const resolvePayload = (): ResetPasswordType => {
        switch (method) {
          case PasswordSubmitMethod.WorkEmail:
            return {
              fLoginUsername,
              fLoginUserEmail: workEmail,
              fSmsPhoneNumber: undefined,
            };
          case PasswordSubmitMethod.SMS:
            return {
              fLoginUsername,
              fLoginUserEmail: undefined,
              fSmsPhoneNumber: mobile,
            };
          case PasswordSubmitMethod.AlternateEmail:
            return {
              fLoginUsername,
              fLoginUserEmail: alternateEmail,
              fSmsPhoneNumber: undefined,
            };
          default:
            return {
              fLoginUsername,
              fLoginUserEmail: undefined,
              fSmsPhoneNumber: undefined,
            };
        }
      };

      return confirmPayload(resolvePayload(), data).then((submitPayload) => {
        return postRestPassword(submitPayload)
          .then((response) => {
            if (response) {
              onSubmitSuccess();
              enqueueSnackbar(t('laPasswordResetOK'), {
                variant: 'success',
              });
            }
          })
          .catch((_error) => {
            enqueueSnackbar(t('errorMsg'), {
              variant: 'error',
            });
          });
      });
    });
  }, [handleSubmit, enqueueSnackbar, onSubmitSuccess, t, confirmPayload]);

  return {
    form,
    onSubmit,
    isFormEnabled: isMethodsAvailable,
    employeeNationality,
    dialogTitle,
    onClose: handleClose,
  };
};
